import React from 'react';
import { Box, Typography, SvgIcon } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { DescriptionOutlined } from '@mui/icons-material';

interface LogoProps {
  variant?: 'light' | 'dark' | 'primary';
  showText?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const Logo: React.FC<LogoProps> = ({ 
  variant = 'primary', 
  showText = true,
  size = 'medium'
}) => {
  // Colors based on variant
  const getColors = () => {
    switch (variant) {
      case 'light':
        return { main: '#ffffff', text: '#ffffff' };
      case 'dark':
        return { main: '#2c3e50', text: '#2c3e50' };
      case 'primary':
      default:
        return { main: '#2196f3', text: '#2c3e50' };
    }
  };

  // Size based on prop
  const getSize = () => {
    switch (size) {
      case 'small':
        return { icon: 24, text: '1rem' };
      case 'large':
        return { icon: 40, text: '1.75rem' };
      case 'medium':
      default:
        return { icon: 32, text: '1.5rem' };
    }
  };

  const colors = getColors();
  const sizeValues = getSize();

  return (
    <Box 
      component={RouterLink} 
      to="/"
      sx={{ 
        display: 'flex', 
        alignItems: 'center',
        textDecoration: 'none',
      }}
    >
      <Box 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          p: 0.5,
          mr: showText ? 1 : 0,
        }}
      >
        <SvgIcon
          sx={{ 
            fontSize: sizeValues.icon,
            color: colors.main,
            filter: variant === 'light' ? 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))' : 'none',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" 
              fill="currentColor" 
            />
            <path 
              d="M14 2V8H20L14 2Z" 
              fill={variant === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)'} 
            />
            <path 
              d="M11.75 14.75C11.75 15.3023 11.3023 15.75 10.75 15.75H9.25C8.69772 15.75 8.25 15.3023 8.25 14.75V13.25C8.25 12.6977 8.69772 12.25 9.25 12.25H10.75C11.3023 12.25 11.75 12.6977 11.75 13.25V14.75Z" 
              fill={variant === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)'} 
            />
            <path 
              d="M15.75 14.75C15.75 15.3023 15.3023 15.75 14.75 15.75H13.25C12.6977 15.75 12.25 15.3023 12.25 14.75V13.25C12.25 12.6977 12.6977 12.25 13.25 12.25H14.75C15.3023 12.25 15.75 12.6977 15.75 13.25V14.75Z" 
              fill={variant === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)'} 
            />
            <path 
              d="M11.75 18.75C11.75 19.3023 11.3023 19.75 10.75 19.75H9.25C8.69772 19.75 8.25 19.3023 8.25 18.75V17.25C8.25 16.6977 8.69772 16.25 9.25 16.25H10.75C11.3023 16.25 11.75 16.6977 11.75 17.25V18.75Z" 
              fill={variant === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)'} 
            />
          </svg>
        </SvgIcon>
      </Box>
      
      {showText && (
        <Typography 
          variant="h6" 
          component="span"
          sx={{ 
            fontWeight: 700,
            fontSize: sizeValues.text,
            color: colors.text,
            letterSpacing: '-0.5px',
            display: 'flex',
          }}
        >
          Contract
          <Box component="span" sx={{ color: colors.main, ml: 0.5 }}>AI</Box>
        </Typography>
      )}
    </Box>
  );
};

export default Logo; 